// ═══════════════════════════════════════════════════════════════
// PHOTO UPLOADER — Reusable component (Batch 14)
// ═══════════════════════════════════════════════════════════════
// Props:
//   userId          — required, the uploading user's ID (for staging path)
//   value           — array of { url, path } currently selected
//   onChange        — (newArray) => void
//   maxFiles        — default 5
//   acceptedTypes   — default images only
//   maxSizeBytes    — default 10MB
//   label           — default "Add photos"
//   required        — visual indicator only (no enforcement)
//   minFiles        — visual indicator (no enforcement; parent validates)
// ═══════════════════════════════════════════════════════════════

import React, { useState, useRef } from 'react';
import {
  uploadToStaging, validateFile,
  ACCEPTED_IMAGE_TYPES, ACCEPTED_ALL_TYPES, MAX_FILE_SIZE_BYTES,
} from '../services/storageService';
import toast from 'react-hot-toast';

export default function PhotoUploader({
  userId,
  value = [],
  onChange,
  maxFiles = 5,
  acceptedTypes = ACCEPTED_IMAGE_TYPES,
  maxSizeBytes = MAX_FILE_SIZE_BYTES,
  label = 'Add photos',
  required = false,
  minFiles = 0,
  helperText = null,
}) {
  const fileInputRef = useRef(null);
  const [uploadingFiles, setUploadingFiles] = useState([]); // [{ name, percent, error }]
  const isMaxReached = value.length >= maxFiles;

  const handlePick = () => {
    if (isMaxReached) {
      toast.error(`Maximum ${maxFiles} files`);
      return;
    }
    fileInputRef.current?.click();
  };

  const handleFilesSelected = async (e) => {
    const selected = Array.from(e.target.files || []);
    e.target.value = ''; // allow re-selecting same file later
    if (selected.length === 0) return;

    const remaining = maxFiles - value.length;
    if (selected.length > remaining) {
      toast.error(`Only ${remaining} more file${remaining !== 1 ? 's' : ''} allowed`);
      return;
    }

    // Pre-validate all files
    for (const file of selected) {
      const v = validateFile(file, { acceptedTypes, maxSizeBytes });
      if (!v.valid) {
        toast.error(`${file.name}: ${v.error}`);
        return;
      }
    }

    // Upload each in parallel, tracking progress per file
    const newProgress = selected.map(f => ({ name: f.name, percent: 0, error: null }));
    setUploadingFiles(newProgress);

    const uploads = selected.map((file, idx) =>
      uploadToStaging(file, userId, {
        acceptedTypes, maxSizeBytes,
        onProgress: (percent) => {
          setUploadingFiles(prev => prev.map((p, i) => i === idx ? { ...p, percent } : p));
        },
      })
        .then(result => ({ ...result, name: file.name, error: null }))
        .catch(err => ({ name: file.name, error: err.message || 'Upload failed' }))
    );

    const results = await Promise.all(uploads);
    setUploadingFiles([]);

    const successes = results.filter(r => !r.error).map(r => ({ url: r.url, path: r.path }));
    const failures = results.filter(r => r.error);

    if (failures.length > 0) {
      failures.forEach(f => toast.error(`${f.name}: ${f.error}`));
    }
    if (successes.length > 0) {
      onChange([...value, ...successes]);
    }
  };

  const handleRemove = (idx) => {
    // Note: we don't delete from staging — Cloud Function cleans up unused stage files
    // during the report submission (only copies referenced ones, leaves orphans for TTL cleanup).
    // [VERIFY: implement staging TTL cleanup as scheduled function — Batch 14 future improvement]
    onChange(value.filter((_, i) => i !== idx));
  };

  const isImage = (url) => {
    // Best-effort: check if URL or path suggests image
    return /\.(jpe?g|png|webp|heic|heif)(\?|$)/i.test(url);
  };

  const accept = acceptedTypes.join(',');

  return (
    <div className="form-group">
      <label className="form-label">
        {label}
        {required && <span style={{ color: '#dc2626' }}> *</span>}
        {(minFiles > 0 || maxFiles) && (
          <span className="font-normal ml-1.5" style={{ fontSize: '0.78rem', color: '#94a3b8' }}>
            ({value.length}/{maxFiles}{minFiles > 0 ? `, min ${minFiles}` : ''})
          </span>
        )}
      </label>

      {helperText && (
        <div className="mb-1.5" style={{ fontSize: '0.78rem', color: '#94a3b8' }}>{helperText}</div>
      )}

      {/* Thumbnails of uploaded files */}
      {value.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-2">
          {value.map((item, idx) => (
            <div key={idx} className="relative rounded-lg overflow-hidden border-[1.5px] border-slate-200" style={{
              width: 80, height: 80, background: '#f8fafc',
            }}>
              {isImage(item.url) ? (
                <img src={item.url} alt={`upload-${idx}`}
                  className="w-full h-full" style={{ objectFit: 'cover' }} />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-center" style={{
                  fontSize: '0.7rem', color: '#5a6578', padding: 4,
                }}>📄 PDF</div>
              )}
              <button onClick={() => handleRemove(idx)} type="button" className="absolute rounded-full border-0 cursor-pointer font-bold p-0 flex items-center justify-center" style={{
                top: 2, right: 2, width: 20, height: 20,
                background: 'rgba(0,0,0,0.6)', color: '#fff', fontSize: '0.7rem',
              }}>×</button>
            </div>
          ))}
        </div>
      )}

      {/* Active uploads */}
      {uploadingFiles.length > 0 && (
        <div className="flex flex-col gap-1 mb-2">
          {uploadingFiles.map((u, idx) => (
            <div key={idx} style={{ fontSize: '0.78rem', color: '#5a6578' }}>
              <div className="flex justify-between mb-0.5">
                <span className="overflow-hidden whitespace-nowrap" style={{ textOverflow: 'ellipsis', maxWidth: 200 }}>
                  {u.name}
                </span>
                <span>{u.percent}%</span>
              </div>
              <div className="w-full overflow-hidden" style={{ height: 4, background: '#e2e8f0', borderRadius: 2 }}>
                <div className="h-full" style={{ width: `${u.percent}%`, background: '#2dd4a8', transition: 'width 0.2s' }} />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add button */}
      {!isMaxReached && (
        <button type="button" onClick={handlePick} className="w-full cursor-pointer font-semibold" style={{
          padding: 12, borderRadius: 10,
          border: '1.5px dashed #2dd4a8', background: '#f0fdfa',
          color: '#0a8270', fontSize: '0.9rem',
        }}>
          + {label} ({value.length}/{maxFiles})
        </button>
      )}

      <input ref={fileInputRef} type="file" accept={accept} multiple={maxFiles > 1}
        onChange={handleFilesSelected} className="hidden" />
    </div>
  );
}
